import { traverseProperty } from "@repo/dynamicEditor/src/helpers/dynamicProperties";
import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { hasValue, PushProcessingNotification, today } from "@repo/helpers";
import { FilterItem, KeyedArray, keyPair, SerializedFile } from "@repo/models";
import { useCallback, useEffect, useMemo } from "react";

import { useBackgroundCheckApi, useBackgroundCheckQueries } from "../../../api/backgroundCheckApi";
import { useRepOnboardingQuery, useTabCountQuery } from "../../../api/repApi";
import { useRepSegmentsApi } from "../../../api/repSegmentsApi";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import { useBackgroundCheckContext, useRepContext } from "../../../contexts/editableItemContext";
import { useSettingsQuery } from "../../../helpers/queryHooks";
import { BackgroundCheck, RepSegment } from "../../../models";
import { tabs } from "../../../string";

export default function BackgroundCheckEditorDynamic(props: Props) {
	const bgcContext = useBackgroundCheckContext();
	const { repId } = useRepContext();
	const { saveBackgroundCheckDocs, saveBackgroundCheck, sendExceptionEmail, loadBgc } = useBackgroundCheckApi();
	const { useBackgroundCheckQuery } = useBackgroundCheckQueries();
	const { clearQuery } = useBackgroundCheckQuery();
	const { checkOnboarding } = useRepOnboardingQuery();
	const { getRepSegment } = useRepSegmentsApi(repId);
	const backgroundCheck = bgcContext.item;
	const savedBgc = bgcContext.savedItem;
	const [bgcLists, listsLoaded] = useBGCLists();
	const [settings, settingsLoaded] = useSettingsQuery();
	const { clearQuery: clearTabCount } = useTabCountQuery(repId);

	const repSegment = useMemo(() => getRepSegment(backgroundCheck?.onboardingSegmentId), [backgroundCheck]);

	const allLoaded = useMemo(
		() => backgroundCheck && listsLoaded && settingsLoaded,
		[backgroundCheck, listsLoaded, settingsLoaded],
	);
	const templateData = useMemo(() => {
		return { backgroundCheck, bgcLists, repSegment };
	}, [backgroundCheck, bgcLists, repSegment]);

	useEffect(() => {
		if (backgroundCheck && allLoaded) {
			const currentlyFailing = backgroundCheck.failingChecks;
			const failingChecks = !!bgcCheckLists.filter(isCheckFailed).length;

			if (backgroundCheck.exceptionStatus != null) return;

			if (currentlyFailing !== failingChecks) {
				bgcContext.updateItem({ failingChecks });
				if (!failingChecks && backgroundCheck.exceptionRequested)
					bgcContext.updateItem({ exceptionRequested: false });
			}
		}
	}, [backgroundCheck, settings, allLoaded]);

	function isCheckFailed(check: string) {
		const value = backgroundCheck[check];
		//Check for explicit false, as nulls just indicate hasn't filled out yet
		if (value !== false) return false;
		const { requiredChecks } = settings.backgroundCheckSettings;
		const allSegments = Object.keys(requiredChecks);
		const segmentId = repSegment?.id || allSegments[0];
		return requiredChecks[segmentId][check];
	}

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Background Check");
		try {
			const id = await saveBackgroundCheck(backgroundCheck);
			notify.processing("Saving Documents...");
			await saveBackgroundCheckDocs(repId, id, backgroundCheck);

			if (backgroundCheck.exceptionRequested && !savedBgc.exceptionRequested) sendExceptionEmail(id);

			notify.processing("Loading...");
			const newBgc = await loadBgc(id);
			bgcContext.overwriteItem(newBgc);
			notify.success("Saved!");

			await clearQuery();
			await clearTabCount();
			await checkOnboarding();
			return newBgc;
		} catch (ex) {
			notify.error("Failed to save");
			throw ex;
		}
	}, [backgroundCheck, savedBgc, saveBackgroundCheckDocs, saveBackgroundCheck, sendExceptionEmail, loadBgc]);

	const updateValues = useMemo(() => updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function updateValueInternal(
		id: string,
		value: any,
		currentState: IBackgroundCheckData,
		parentComponent?: string,
	) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return (newState ?? currentState) as IBackgroundCheckData;
	}
	async function applyBusinessLogic(id: string, value: any, newState: IBackgroundCheckData) {
		async function update(name: string, value: any) {
			newState = await updateValueInternal(name, value, newState);
		}
		const clear = async (name: string) => await update(name, null);
		//Custom logic
		switch (id) {
			case "backgroundCheck.exceptionStatusId":
				await update("backgroundCheck.exceptionStatus", value);
				break;
			case "backgroundCheck.consentToConduct":
				if (value && !hasValue(newState.backgroundCheck.requestDate))
					await update("backgroundCheck.requestDate", today());
				break;
			case "backgroundCheck.responseStarted":
				if (value && !hasValue(newState.backgroundCheck.responseDate))
					await update("backgroundCheck.responseDate", today());
				break;
			case "backgroundCheck.responseDocument":
			case "backgroundCheck.consentDocument":
			case "backgroundCheck.exceptionDocument":
			case "backgroundCheck.driversLicenseDocument":
			case "backgroundCheck.driversLicenseDocumentBack":
				await handleDocument();
				break;
			case "backgroundCheck.screeningProviderName":
				await clear("backgroundCheck.screeningProviderOther");
				break;
		}

		return newState as IBackgroundCheckData;

		async function handleDocument() {
			const existingProp = id + "Uploaded";
			const existing = traverseProperty(existingProp, newState) as SerializedFile[];
			if (existing?.length) await clear(existingProp);
		}
	}

	return (
		<>
			<EditableItemDynamicTab
				itemContext={bgcContext}
				pageName={tabs.bgChecks}
				otherTemplateData={templateData}
				itemProp="backgroundCheck"
				updateValues={updateValues}
				save={save}
				loaded={allLoaded}
				readonly={props.readOnly}
			/>
		</>
	);
}

export interface IBackgroundCheckData extends ITemplateData {
	backgroundCheck: BackgroundCheck;
	repSegment: RepSegment;
	activeBGCLists?: IBackgroundCheckList;
}

const bgcCheckListsDefault = [
	"passedCriminalHistoryCheck",
	"passedFederalCriminalCheck",
	"passedNationalCriminalCheck",
	"passedProhibitedPartiesListsCheck",
	"passedSexOffenderRegistriesListCheck",
	"passedSSNTrace",
];

const bgcCheckListsOptional = [
	"passedStateSpecificCheck",
	"passedStateCriminalCheck",
	"passedAllCountyCheck",
	"passedDRCOffenderCheck",
];

const bgcCheckLists = [...bgcCheckListsDefault, ...bgcCheckListsOptional];

export function useBGCLists() {
	const { repId } = useRepContext();
	const { useScreeningProvidersQuery } = useBackgroundCheckQueries();
	const { useOnboardingSegmentsQuery } = useRepSegmentsApi(repId);
	const { onboardingSegments, loaded: representativeSegmentsLoaded } = useOnboardingSegmentsQuery();
	const { data: screeningProviders, loaded: screeningProvidersLoaded } = useScreeningProvidersQuery();
	const loaded = screeningProvidersLoaded && representativeSegmentsLoaded;
	const activeLists = useMemo(() => {
		return {
			screeningProviders: getItems(screeningProviders, screeningProvidersLoaded),
			exceptionStatuses,
			onboardingSegments,
		} as IBackgroundCheckList;
	}, [screeningProvidersLoaded, representativeSegmentsLoaded]);

	return [activeLists, loaded] as [IBackgroundCheckList, boolean];

	function getItems(allItems: KeyedArray<FilterItem>, loaded: boolean, forceInclude?: FilterItem) {
		if (!loaded) return null as keyPair[];
		if (forceInclude) allItems.set(forceInclude);
		return allItems.map((c) => {
			return { id: c.id, value: c.name } as keyPair;
		});
	}
}

interface IBackgroundCheckList {
	screeningProviders: keyPair[];
	exceptionStatuses: keyPair[];
	onboardingSegments: keyPair[];
}

const exceptionStatuses = [
	{ id: 3, value: "Approved" },
	{ id: 2, value: "Denied" },
	{ id: 1, value: "Pending" },
];

interface Props {
	readOnly?: boolean;
}
